import { toJS, when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import type { IFormNotificationSettings } from '@feathr/blackbox';
import { useBlackboxVersion } from '@feathr/extender/hooks';
import { useAccount, useStore, useUser } from '@feathr/extender/state';

import { useIframeMessaging } from '../../../hooks';
import { useDonationFormBuilder } from '../useDonationFormBuilder';
import { getEditorUrl } from './EditorIframe.utils';

import * as styles from './EditorIframe.css';

interface IEditorConfigMessage {
  type: 'feathr:editor:config';
  config: Record<string, unknown>;
  notification_settings?: IFormNotificationSettings;
  isDirty: boolean;
  isValid?: boolean;
}

interface IEditorReadyMessage {
  type: 'feathr:editor:ready';
}

interface IFormsListRequestMessage {
  type: 'feathr:forms:request';
  excludeFormId?: string;
  page?: number;
}

function isEditorConfigMessage(data: unknown): data is IEditorConfigMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    data.type === 'feathr:editor:config'
  );
}

function isEditorReadyMessage(data: unknown): data is IEditorReadyMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    data.type === 'feathr:editor:ready'
  );
}

function isFontsMessage(data: unknown): boolean {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    (data.type === 'feathr:fonts:request' || data.type === 'feathr:fonts:refresh')
  );
}

function isFormsListRequestMessage(data: unknown): data is IFormsListRequestMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    data.type === 'feathr:forms:request'
  );
}

function EditorIframe(): JSX.Element {
  const { donationForm, setEditorConfig } = useDonationFormBuilder();

  // Determine if user is a developer (same logic as Development & QA nav link)
  const account = useAccount();
  const user = useUser();
  const { DonationForms, Fonts } = useStore();
  const { isOnProduction } = useBlackboxVersion();
  const showDevelopmentNavUserSetting = user.getSetting('show_dev_nav') ?? false;
  const isDeveloper = !isOnProduction && (account.isTestAccount || showDevelopmentNavUserSetting);

  const { url: editorUrl, origin: editorOrigin } = getEditorUrl(isDeveloper);

  const location = useLocation();
  const history = useHistory();

  const sendConfigToEditor = useCallback(async (): Promise<void> => {
    let config = toJS(donationForm.get('config') ?? {}) as unknown as Record<string, unknown>;
    const notificationSettings = toJS(donationForm.get('notification_settings'));

    // Check if this is a new form that should inherit default styling
    const searchParams = new URLSearchParams(location.search);
    const isNewForm = searchParams.get('newForm') === 'true';

    if (isNewForm) {
      // Clear the URL param to avoid re-applying on future editor reloads
      searchParams.delete('newForm');
      const newSearch = searchParams.toString();
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : '',
      });

      // Fetch forms to find the one with defaultStyling enabled
      const formsList = DonationForms.list({ pagination: { page_size: 100 } });
      await when(() => !formsList.isPending);

      const defaultForm = formsList.models.find((form) => {
        const formConfig = form.get('config') as unknown as Record<string, unknown> | undefined;
        const settings = formConfig?.settings as Record<string, unknown> | undefined;
        return settings?.defaultStyling === true;
      });

      if (defaultForm) {
        const defaultConfig = toJS(defaultForm.get('config')) as unknown as Record<string, unknown>;
        if (defaultConfig?.style) {
          config = {
            ...config,
            style: defaultConfig.style,
          };

          // Persist the default style to the database so it survives navigation
          donationForm.set({ config: config as unknown as typeof donationForm.attributes.config });
          await donationForm.patchDirty();
        }
      }
    }

    iframeRef.current?.contentWindow?.postMessage(
      {
        type: 'feathr:editor:init',
        config,
        notification_settings: notificationSettings,
        formId: donationForm.id,
        userEmail: user.get('email'),
        showExtendedFundraisingFields: true,
      },
      editorOrigin,
    );
  }, [
    DonationForms,
    donationForm,
    editorOrigin,
    history,
    location.pathname,
    location.search,
    user,
  ]);

  const sendFontsToEditor = useCallback(async (): Promise<void> => {
    const fontsList = Fonts.list();
    await when(() => !fontsList.isPending);

    const accountFonts = fontsList.models.map((font) => ({
      name: font.name,
      family: `'${font.name}', Arial, Helvetica, sans-serif`,
      url: `${BLACKBOX_URL}fonts/${font.id}/css`,
    }));

    iframeRef.current?.contentWindow?.postMessage(
      {
        type: 'feathr:fonts:data',
        payload: { accountFonts },
      },
      editorOrigin,
    );
  }, [Fonts, editorOrigin]);

  const sendFormsListToEditor = useCallback(
    async (excludeFormId?: string, page = 1): Promise<void> => {
      const formsList = DonationForms.list({ pagination: { page_size: 20, page } });
      await when(() => !formsList.isPending);

      const forms = formsList.models
        .filter((form) => form.id !== excludeFormId)
        .map((form) => {
          const config = toJS(form.get('config')) as unknown as Record<string, unknown> | undefined;
          return {
            id: form.id,
            name: form.name,
            style: config?.style,
          };
        });

      const { page: currentPage, pages, count } = formsList.pagination;

      iframeRef.current?.contentWindow?.postMessage(
        {
          type: 'feathr:forms:data',
          payload: {
            forms,
            pagination: { page: currentPage, pages, count },
          },
        },
        editorOrigin,
      );
    },
    [DonationForms, editorOrigin],
  );

  const handleMessage = useCallback(
    (event: MessageEvent): void => {
      if (isEditorReadyMessage(event.data)) {
        sendConfigToEditor();
        return;
      }

      if (isFontsMessage(event.data)) {
        sendFontsToEditor();
        return;
      }

      if (isFormsListRequestMessage(event.data)) {
        sendFormsListToEditor(event.data.excludeFormId, event.data.page);
        return;
      }

      if (isEditorConfigMessage(event.data)) {
        setEditorConfig(
          event.data.config,
          event.data.notification_settings,
          event.data.isDirty,
          event.data.isValid ?? true,
        );
      }
    },
    [sendConfigToEditor, sendFontsToEditor, sendFormsListToEditor, setEditorConfig],
  );

  const { iframeRef } = useIframeMessaging({
    origin: editorOrigin,
    onMessage: handleMessage,
    sendConfig: sendConfigToEditor,
  });

  return (
    <div className={styles.root}>
      <iframe
        className={styles.iframe}
        ref={iframeRef}
        src={editorUrl}
        title={'Donation Form Editor'}
      />
    </div>
  );
}

export default observer(EditorIframe);
